// @spec SHL-STR-022
'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal/modal';
import { ShortcutKey } from '@/components/shortcuts/shortcut-key';
import type { TranslateFn } from '@/lib/i18n';
import {
  getShortcutsByCategory,
  getShortcutCategoryLabel,
  SHORTCUT_CATEGORY_ORDER,
  type KeyboardShortcut,
  type ShortcutCategory,
} from '@/lib/keyboard-shortcuts';
import { cn } from '@/lib/utils';

export interface KeyboardShortcutsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog component that displays all available keyboard shortcuts.
 *
 * Features:
 * - Groups shortcuts by category (Navigation, Search, Actions, etc.)
 * - Shows OS-appropriate key symbols
 * - Responsive layout (single column on mobile, two columns on desktop)
 * - Accessible with proper ARIA attributes
 *
 * @example
 * const [open, setOpen] = useState(false);
 *
 * <KeyboardShortcutsDialog
 *   open={open}
 *   onOpenChange={setOpen}
 * />
 */
export function KeyboardShortcutsDialog({ open, onOpenChange }: KeyboardShortcutsDialogProps) {
  const { t } = useTranslation('common');
  const translate: TranslateFn = t;
  const shortcutsByCategory = React.useMemo(() => getShortcutsByCategory(), []);

  // Get categories in the defined order, filtering to only those with shortcuts
  const orderedCategories = SHORTCUT_CATEGORY_ORDER.filter((category) =>
    shortcutsByCategory.has(category)
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={translate('header.keyboardShortcuts')}
      description={translate('header.keyboardShortcutsDescription')}
      size="lg"
    >
      <div className="grid gap-6 md:grid-cols-2">
        {orderedCategories.map((category) => {
          const shortcuts = shortcutsByCategory.get(category);
          if (!shortcuts || shortcuts.length === 0) return null;

          return <ShortcutCategory key={category} category={category} shortcuts={shortcuts} />;
        })}
      </div>
    </Modal>
  );
}

interface ShortcutCategoryProps {
  category: ShortcutCategory;
  shortcuts: KeyboardShortcut[];
}

/**
 * Renders a category section with its shortcuts.
 */
function ShortcutCategory({ category, shortcuts }: ShortcutCategoryProps) {
  const { t } = useTranslation('common');
  const translate: TranslateFn = t;
  const label = getShortcutCategoryLabel(category, translate);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{label}</h3>
      <div className="space-y-1">
        {shortcuts.map((shortcut) => (
          <ShortcutItem key={shortcut.id} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
}

interface ShortcutItemProps {
  shortcut: KeyboardShortcut;
}

/**
 * Renders a single shortcut row with description and key combination.
 */
function ShortcutItem({ shortcut }: ShortcutItemProps) {
  // For display, show only the first key combo if there are multiple
  // (e.g., "ctrl+k" instead of ["ctrl+k", "mod+k"])
  const displayKeys: string = Array.isArray(shortcut.keys)
    ? (shortcut.keys[0] ?? '')
    : shortcut.keys;

  if (!displayKeys) return null;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-md px-2 py-1.5',
        'hover:bg-muted/50 transition-colors'
      )}
    >
      <span className="text-sm text-muted-foreground">{shortcut.description}</span>
      <ShortcutKey keys={displayKeys} />
    </div>
  );
}
